import React, { ReactNode, useEffect, useMemo, useState } from "react";

// UX
import CardLabOrderInterfaceUX from "./CardLabOrderInterfaceUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Utils
import { Checkbox } from "semantic-ui-react";
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  SaveType,
  FilterSearchType,
  WidthsType,
  GenerateTableHeader,
} from "./sequence/LabOrderInterface";

// Serializer
import LabOrderInterfaceSerializer from "issara-sdk/types/CentralLabOrderListSerializer_apps_LAB";

// Type
type CardLabOrderInterfaceProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  LabOrderInterfaceSequence: State["LabOrderInterfaceSequence"];
  languageUX: "en" | "th";
} & PickedProps;

const CardLabOrderInterface = (props: CardLabOrderInterfaceProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedLabOrder, setSelectedLabOrder] = useState<any[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "LabOrderInterface", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data ?? ""}</div>;
  };

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      let newData = (props.LabOrderInterfaceSequence?.LabOrderInterfaceList || []).map(
        (item: LabOrderInterfaceSerializer) => item.id
      );

      setSelectedLabOrder(newData);
    } else {
      setSelectedLabOrder([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "LabOrderInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      page: 0,
    });

    setSelectedLabOrder([]);
  };

  const handleSave = (type: SaveType) => {
    props.runSequence({
      sequence: "LabOrderInterface",
      action: "SAVE",
      selectedItems: selectedLabOrder,
      cardKey: CARD_NAME,
      type,
    });

    setSelectedLabOrder([]);
  };

  const handleClear = () => {
    props.runSequence({
      sequence: "LabOrderInterface",
      action: "CLEAR",
      cardKey: CARD_NAME,
    });

    setSelectedLabOrder([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;
    props.setProp(`LabOrderInterfaceSequence.searchLabOrder.${name}`, data?.value);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`LabOrderInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "LabOrderInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      [key]: value,
    });

    setSelectedLabOrder([]);
    props.setProp(`LabOrderInterfaceSequence.selectedItems`, []);
  };

  const handleDisabledTrigger = useMemo(() => {
    if (selectedLabOrder.length === 0) return true;
  }, [selectedLabOrder, props.LabOrderInterfaceSequence?.LabOrderInterfaceList]);

  // useMemo
  const labOrderInterfaceList = useMemo(() => {
    return (props.LabOrderInterfaceSequence?.LabOrderInterfaceList || []).map(
      (item: LabOrderInterfaceSerializer) => {

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={selectedLabOrder.includes(item.id)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  setSelectedLabOrder([...selectedLabOrder, item.id]);
                } else {
                  let newData = selectedLabOrder.filter((key: any) => key !== item.id);
                  setSelectedLabOrder(newData);
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof LabOrderInterfaceSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              if (key === "order_name") {
                data[key] = columnTableCenter(
                  <div dangerouslySetInnerHTML={{ __html: item[key] || "" }}/>
                )
              } else if (["order_receive_time", "order_approve_time", "order_perform_time", "order_ack_time"].includes(key)) {
                data[key] = columnTableCenter(item[key] ? dateToStringBE(item[key]) : item[key]);
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            }, {}
          ),
        };
      }
    );
  }, [
    selectedLabOrder,
    props.LabOrderInterfaceSequence?.LabOrderInterfaceList,
    tableHeaders,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardLabOrderInterfaceUX
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchLabOrder={props.LabOrderInterfaceSequence?.searchLabOrder}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedLabOrder.length ===
                    (props.LabOrderInterfaceSequence?.LabOrderInterfaceList || [])
                      .length &&
                  (props.LabOrderInterfaceSequence?.LabOrderInterfaceList || [])
                    .length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        labOrderInterfaceList={labOrderInterfaceList}
        paginatedItems={props.LabOrderInterfaceSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title={intl.formatMessage({ id: "ค้นหา" })}
            name="SEARCH"
            size="medium"
          />
        }
        triggerButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("TRIGGER")}
            disabled={handleDisabledTrigger}
            // data
            paramKey={`${CARD_NAME}_TRIGGER`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_TRIGGER`]}
            // config
            color="blue"
            title="Trigger"
            name="TRIGGER"
            size="medium"
          />
        }
      />
    </>
  );
};

CardLabOrderInterface.displayName = "CardLabOrderInterface";

export default React.memo(CardLabOrderInterface);

const columnWidths: WidthsType = {
  id: 70,
  status_name: 120,
  payment_status_name: 120,
  order_name: 200,
  patient_name: 200,
  doctor_name: 200,
};

const tableHeaders = GenerateTableHeader(columnWidths);
